import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import exportVirtualBatteriesService from "../services/exportAsFile";
import { default as vbTypesService} from "../services/types";
import { default as vbPercentagesService} from "../services/percentages";
import { default as vbMoveService} from "../services/move";
import contractActions from "@/components/Contracts/actions/newActions";
import {actions as bvActions} from "../slices/virtualBattery";
import saveAs from 'file-saver';
import { useAppDispatch, useAppSelector } from "@/hooks";
import { VirtualBatteryJoinTypes } from "../types/virtualBattery";
import { BatteryDistributionsMap, Distribution } from "../types/percentages";
import { useContracts } from "@/components/Contracts/hooks/useContracts";
import { IElecContract } from "@/components/Contracts/types/contracts";

export const useVirtualBatteries = () => {
  const { t } = useTranslation();
  const Settings = useSettingsContext();
  const dispatch = useAppDispatch();
  const {currentContract, contracts, fetchContracts} = useContracts("electricity");

  const virtualBatteryTypes = useAppSelector(state => state.virtualBattery.virtualBatteryTypes);
  const isWaitingChangeRequest = useAppSelector(state => state.virtualBattery.isWaitingChangeRequest);
  const isFetchingBateriaVirtualTypes = useAppSelector(state => state.virtualBattery.isFetchingBateriaVirtualTypes);
  const isFetchingBateriaVirtualPercentages = useAppSelector(state => state.virtualBattery.isFetchingBateriaVirtualPercentages);
  const bateriaVirtualPercentages = useAppSelector(state => state.virtualBattery.bateriaVirtualPercentages);

  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!virtualBatteryTypes.length && !isFetchingBateriaVirtualTypes) {
      fetchVirtualBatteryTypes();
    }
  }, []);

  const getVirtualBatteryFromId = (polissaBateriaVirtualId: number) => {
    let searchedVirtualBattery: NonNullable<IElecContract["bateria_virtual"]>[number] | undefined = undefined;

    for (const contract of contracts) {

      if (contract.bateria_virtual) {

        for (const bat of contract.bateria_virtual) {

          if (bat.id === polissaBateriaVirtualId) {
            searchedVirtualBattery = bat;
            break; // Break out of the inner loop
          }
        }
        
        // If a match was found in this contract's bateria_virtual array, break out of the outer loop
        if (searchedVirtualBattery) {
          break;
        }
      }
    }

    return searchedVirtualBattery;
  };


  // Virtual Batteries
  const isBateriaVirtualActive = (polissaBateriaVirtualId: number) => {
    /**
     * Determines if a "bateria virtual contract" record is active based on its date configuration.
     *
     * A record is considered active if:
     * - Today's date is inside the configured `data_inici` and `data_final` range.
     * - `data_final` is not set, and `data_inici` is earlier than today's date.
     *
     * @returns {boolean} - `true` if the record is active, `false` otherwise.
     */
    const bateriaVirtualContract = getVirtualBatteryFromId(polissaBateriaVirtualId);
    if (bateriaVirtualContract) {
      const today = new Date();
      const dataInici = new Date(bateriaVirtualContract.data_inici);
      const dataFinal = bateriaVirtualContract.data_final ? new Date(bateriaVirtualContract.data_final) : null;
  
      if (dataFinal) {
        return today >= dataInici && today <= dataFinal;
      }
      return today >= dataInici;
    } else {
      throw new Error("Battery not found in current contract");
    }
  }

  const getAllBateriaVirtualsFromContracts = (excludeContractName: string) => {
    let all = [];

    for (const contract of contracts) {
      if (contract.name != excludeContractName && contract.bateria_virtual) {
        for (const bateriaVirtual of contract.bateria_virtual) {
          all.push(bateriaVirtual);
        }
      }
    }
    return all;
  };

  // Percentage
  const fetchPercentages = (bateriaId: number) => {
    vbPercentagesService.fetchPercentages(bateriaId)
    .then(res => {
      dispatch(bvActions.percentagesReceived(res.promise.data));
    })
  }

  const setBatteryPercentage = async (
    contractName: string,
    bateriaId: number,
    percentage: number
  ) => {
    dispatch(bvActions.percentageSetRequested());
    await vbPercentagesService.changePercentage(
      contractName, bateriaId, percentage
    ).then(res => {
      dispatch(bvActions.percentageSetDone())
    }).catch(err => {
      dispatch(bvActions.percentageSetError())
    })
  }

  const sortDistribution = (
    distribution: Distribution[], 
    sortBy: keyof Pick<Distribution, "contractName" | "pes" | "pesPercentage"> = "contractName"
  ) => {
    return distribution?.sort((a, b) => {
      if (a[sortBy] < b[sortBy]) return -1;
      if (a[sortBy] > b[sortBy]) return 1;
      return 0;
    }) ?? distribution;
  }

  const getBatteriesDistribution = (excludeContractName?: string, excludeBatteryId?: number) => {
    let groupedBatteryDistribution: BatteryDistributionsMap = {};

    for (const contract of contracts) {
      if (contract.name != excludeContractName && contract.bateria_virtual) {
        for (const bateriaVirtual of contract.bateria_virtual) {
          const batteryDistribution = {
            contractId: contract.id,
            pes: bateriaVirtual.pes,
            pesPercentage: bateriaVirtual.pes_percentatge,
            contractName: contract.name,
            isBateriaVirtualActive: isBateriaVirtualActive(bateriaVirtual.id)
          }
          if (bateriaVirtual.bateria.id in groupedBatteryDistribution) {
            groupedBatteryDistribution[bateriaVirtual.bateria.id].distribution.push(batteryDistribution);
          } else {
            groupedBatteryDistribution[bateriaVirtual.bateria.id] = {
              batteryId: bateriaVirtual.bateria.id,
              batteryName: bateriaVirtual.bateria.name,
              distribution: [ batteryDistribution ]
            };
          }
        }
      }
    }

    if (excludeBatteryId && excludeBatteryId in groupedBatteryDistribution) {
      delete groupedBatteryDistribution[excludeBatteryId];
    }

    // INFO: Sort the distributions (is not optimized, the stress, sorry, right now I don't care if goes slow, you can fix it)
    for (const batteryDistribution of Object.values(groupedBatteryDistribution)) {
      batteryDistribution.distribution = sortDistribution(batteryDistribution.distribution);
    }
    return groupedBatteryDistribution;
  };

  const getBatteryDistribution = (batteryId: number) => {
    const allDistributted = getBatteriesDistribution();
    return allDistributted[batteryId];
  }


  // Types
  const fetchVirtualBatteryTypes = () => {
    dispatch(bvActions.typesRequested());
    vbTypesService.fetchTypes()
    .then(res => {
      dispatch(bvActions.typesReceived(res.promise.data));
    })
  }

  const handleBatteryTypeChange = (
    value: VirtualBatteryJoinTypes, 
    polissaBateriaVirtualId: number
  ) => {
    // dispatch({
    //   type: UPDATING_VIRTUAL_BATTERY_TYPES,
    //   payload: {
    //     loading: true
    //   }
    // });
    const polissaBateriaVirtual = getVirtualBatteryFromId(polissaBateriaVirtualId);
    polissaBateriaVirtual && vbTypesService.changeType(
      currentContract.name, 
      polissaBateriaVirtual.bateria.id,
      value 
    )
    .then(() => {
      dispatch(contractActions.electricity.virtualBatteryTypeUpdated({
        selectedValue: value,
        polissaBateriaVirtualId
      }));
    });
  }


  // Move
  const moveVirtualBatteries = async (
    contractName: string,
    bateriaOrigenId: number, 
    bateriaDestiId: number, 
    percentatge: number
  ) => {
    dispatch(bvActions.batteryMoveRequest());
    vbMoveService.moveVirtualBatteries(
      contractName,
      bateriaOrigenId, 
      bateriaDestiId, 
      percentatge
    ).then(res => {
      dispatch(bvActions.batteryMoveDone());
      fetchContracts();
      // dispatch(contractActions.electricity.contractUpdated(res.promise.data));
    }).catch(err => {
      dispatch(bvActions.batteryMoveError());
    })
  }


  // Download 
  const exportVirtualBatteries = (batteries: string) => {
    setExporting(true);
    return exportVirtualBatteriesService(batteries)
    .then(response => {
      if (Settings?.invoices?.downloadInNewTab ?? false) {
        let newTab = window.open(t('common:url.loading'), '_blank');
        if (newTab) {
            newTab.location.href = URL.createObjectURL(response.promise.data);
          }
      } else {
        const filename = response.promise.headers["content-disposition"].split("=");
        saveAs(response.promise.data, filename[1]);
      }
    })
    .catch(error => {
      setError(true)
    })
    .finally(() => {
      setExporting(false);
    });
  };



  return {
    bateriaVirtualPercentages,
    isFetchingBateriaVirtualPercentages,
    isWaitingChangeRequest,
    error,
    exporting,
    virtualBatteryTypes,
    exportVirtualBatteries,
    fetchPercentages,
    isBateriaVirtualActive,
    getAllBateriaVirtualsFromContracts,
    getBatteriesDistribution,
    getBatteryDistribution,
    getVirtualBatteryFromId,
    handleBatteryTypeChange,
    moveVirtualBatteries,
    setBatteryPercentage,
    sortDistribution,
  }
}